import { FC, useEffect } from 'react';
import { useForm, FormProvider, DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, Typography, FormControlLabel, Checkbox } from '@mui/material';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../../components/controlledComponents/ControlledDateField.component';
import {
  TRouteAssignment,
  TViewType,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useRoutesAndScheduleState,
  useRoutesAndScheduleActions,
} from '../../../contexts/routesAndSchedule.context';
import {
  isIsoDateRangeOrdered,
  zYearFromInput,
  zDateStringFromInput,
  zOptionalDateStringFromInput,
} from 'src/utils/zodFormHelpers';

function parseAcademicYearForForm(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

const getAssignmentFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
      routeId: z.string().min(1, translateText('common.fieldRequired')),
      vehicleId: z.string().min(1, translateText('common.fieldRequired')),
      driverId: z.string().min(1, translateText('common.fieldRequired')),
      academicYear: zYearFromInput({
        message:
          translateText('administration.routesAndSchedule.academicYearInvalid') || undefined,
      }),
      isActive: z.boolean(),
      startDate: zDateStringFromInput({
        requiredMessage: translateText('common.fieldRequired') || undefined,
        message: translateText('common.dateInvalid') || undefined,
      }),
      endDate: zOptionalDateStringFromInput({
        message: translateText('common.dateInvalid') || undefined,
      }),
    })
    .refine(
      (data) => isIsoDateRangeOrdered(data.startDate, data.endDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['endDate'],
      }
    );

type AssignmentFormData = z.infer<ReturnType<typeof getAssignmentFormSchema>>;

const AddEditAssignmentDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const assignmentFormSchema = getAssignmentFormSchema(translateText);
  const { selectedAssignment, editMode, viewType, routes, vehicles, drivers } =
    useRoutesAndScheduleState();
  const { setEditMode, setViewType, addAssignment, updateAssignment } =
    useRoutesAndScheduleActions();

  const isEditMode = editMode === 'edit' && viewType === TViewType.ASSIGNMENTS;
  const isCreateMode =
    editMode === 'create' && viewType === TViewType.ASSIGNMENTS;
  const isOpen =
    (isEditMode || isCreateMode) && viewType === TViewType.ASSIGNMENTS;

  const defaultValues: DefaultValues<AssignmentFormData> = {
    routeId: '',
    vehicleId: '',
    driverId: '',
    academicYear: undefined,
    isActive: true,
    startDate: '',
    endDate: '',
  };

  const methods = useForm<AssignmentFormData>({
    resolver: zodResolver(assignmentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && selectedAssignment) {
        reset({
          routeId: selectedAssignment.routeId || '',
          vehicleId: selectedAssignment.vehicleId || '',
          driverId: selectedAssignment.driverId || '',
          academicYear: parseAcademicYearForForm(selectedAssignment.academicYear),
          isActive: selectedAssignment.isActive ?? true,
          startDate: selectedAssignment.startDate
            ? new Date(selectedAssignment.startDate).toISOString().split('T')[0]
            : '',
          endDate: selectedAssignment.endDate
            ? new Date(selectedAssignment.endDate).toISOString().split('T')[0]
            : '',
        });
      } else if (isCreateMode) {
        reset(defaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignment, isEditMode, isCreateMode, isOpen, reset]);

  const onSubmitForm = (data: AssignmentFormData) => {
    const assignmentData: TRouteAssignment = {
      id: selectedAssignment?.id || '',
      routeId: data.routeId,
      vehicleId: data.vehicleId,
      driverId: data.driverId,
      assignedStudents: selectedAssignment?.assignedStudents || [],
      academicYear: data.academicYear?.toString() || '',
      isActive: data.isActive,
      startDate: new Date(data.startDate),
      endDate: data.endDate ? new Date(data.endDate) : undefined,
    };

    if (isEditMode) {
      updateAssignment(assignmentData);
    } else {
      addAssignment(assignmentData);
    }
    setEditMode(undefined);
    setViewType(undefined);
  };

  const handleClose = () => {
    setEditMode(undefined);
    setViewType(undefined);
  };

  const routeOptions = routes.map((route) => ({
    key: route.id,
    value: route.routeName,
  }));

  const vehicleOptions = vehicles.map((vehicle) => ({
    key: vehicle.id,
    value: vehicle.vehicleNumber,
  }));

  const driverOptions = drivers.map((driver) => ({
    key: driver.id,
    value: driver.name,
  }));

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="add-edit-assignment-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText(
              'administration.routesAndSchedule.editAssignment'
            ) || 'Edit Assignment'
            : translateText('administration.routesAndSchedule.addAssignment') ||
            'Add Assignment'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={false}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="assignment-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="assignment-form-container"
            >
              <Grid item xs={12} data-testid="assignment-basic-section">
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.routesAndSchedule.details.basicInfo'
                  ) || 'Basic Information'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="assignment-academic-year-field"
              >
                <ControlledTextField
                  control={control}
                  name="academicYear"
                  id="assignment-academic-year"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.academicYear'
                    ) || 'Academic Year'
                  }
                  errorMessage={errors.academicYear?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="assignment-status-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isActive')}
                      onChange={(e) => {
                        methods.setValue('isActive', e.target.checked);
                      }}
                    />
                  }
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.status'
                    ) || 'Active'
                  }
                />
              </Grid>

              <Grid item xs={12} data-testid="assignment-assignments-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.assignments'
                  ) || 'Assignments'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4} data-testid="assignment-route-field">
                <ControlledSelectField
                  control={control}
                  name="routeId"
                  id="assignment-route"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.route'
                    ) || 'Route'
                  }
                  itemList={routeOptions}
                  errorMessage={errors.routeId?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={4} data-testid="assignment-vehicle-field">
                <ControlledSelectField
                  control={control}
                  name="vehicleId"
                  id="assignment-vehicle"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.vehicle'
                    ) || 'Vehicle'
                  }
                  itemList={vehicleOptions}
                  errorMessage={errors.vehicleId?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={4} data-testid="assignment-driver-field">
                <ControlledSelectField
                  control={control}
                  name="driverId"
                  id="assignment-driver"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.driver'
                    ) || 'Driver'
                  }
                  itemList={driverOptions}
                  errorMessage={errors.driverId?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="assignment-dates-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText('administration.routesAndSchedule.dates') ||
                    'Dates'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="assignment-start-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="startDate"
                  id="assignment-start-date"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.startDate'
                    ) || 'Start Date'
                  }
                  errorMessage={errors.startDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="assignment-end-date-field">
                <ControlledDateField
                  control={control}
                  name="endDate"
                  id="assignment-end-date"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.endDate'
                    ) || 'End Date (Optional)'
                  }
                  errorMessage={errors.endDate?.message}
                  isGridItem={false}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditAssignmentDetailsView;
